'use client'

import { useNavigate } from 'react-router-dom'
import {
    MdLocalHospital,
    MdBed,
    MdPerson,
    MdCleaningServices,
    MdSwapHoriz,
    MdMedication,
    MdMonitorHeart,
    MdWaterDrop,
    MdDescription,
    MdExitToApp,
    MdDashboard,
    MdArrowForward,
} from 'react-icons/md'
import { HospitalSelector } from '@/components/HospitalSelector'
import { useHospitals } from '@/hooks/useHospitals'

interface HospitalCard {
    id: string
    name: string
    description: string
    icon: React.ReactNode
    route: string
    color: string
}

export default function HospitalPage() {
    const navigate = useNavigate()
    const { hospitals, loading: loadingHospitals, selectedHospitalId, setSelected } = useHospitals()

    const hospitalCards: HospitalCard[] = [
        {
            id: 'dashboard',
            name: 'Dashboard',
            description:
                'Visão geral das internações, ocupação de leitos e alertas',
            icon: <MdDashboard className="text-4xl" />,
            route: '/hospital/dashboard',
            color: 'from-blue-500 to-blue-600',
        },
        {
            id: 'units',
            name: 'Unidades',
            description:
                'Gerencie unidades hospitalares como UTI, enfermarias e centros cirúrgicos',
            icon: <MdLocalHospital className="text-4xl" />,
            route: '/hospital/unidades',
            color: 'from-purple-500 to-purple-600',
        },
        {
            id: 'beds',
            name: 'Leitos',
            description:
                'Controle de leitos, disponibilidade, características e ocupação',
            icon: <MdBed className="text-4xl" />,
            route: '/hospital/beds',
            color: 'from-green-500 to-green-600',
        },
        {
            id: 'admissions',
            name: 'Internações',
            description:
                'Gerencie internações de pacientes, acompanhantes e convênios',
            icon: <MdPerson className="text-4xl" />,
            route: '/hospital/admissions',
            color: 'from-orange-500 to-orange-600',
        },
        {
            id: 'cleaning',
            name: 'Higienização',
            description:
                'Solicitação e acompanhamento de limpeza terminal e concorrente',
            icon: <MdCleaningServices className="text-4xl" />,
            route: '/hospital/cleaning',
            color: 'from-cyan-500 to-cyan-600',
        },
        {
            id: 'transfers',
            name: 'Transferências',
            description:
                'Transferências internas entre unidades e externas para outros hospitais',
            icon: <MdSwapHoriz className="text-4xl" />,
            route: '/hospital/transfers',
            color: 'from-amber-500 to-amber-600',
        },
        {
            id: 'prescriptions',
            name: 'Prescrições',
            description:
                'Prescrições hospitalares com aprazamento e checagem de medicamentos',
            icon: <MdMedication className="text-4xl" />,
            route: '/hospital/prescricoes',
            color: 'from-red-500 to-red-600',
        },
        {
            id: 'vital-signs',
            name: 'Sinais Vitais',
            description:
                'Registro e monitoramento de sinais vitais com alertas automáticos',
            icon: <MdMonitorHeart className="text-4xl" />,
            route: '/hospital/vital-signs',
            color: 'from-pink-500 to-pink-600',
        },
        {
            id: 'fluid-balance',
            name: 'Balanço Hídrico',
            description:
                'Controle de ingestão e eliminação de líquidos por plantão',
            icon: <MdWaterDrop className="text-4xl" />,
            route: '/hospital/fluid-balance',
            color: 'from-sky-500 to-sky-600',
        },
        {
            id: 'nursing-evolution',
            name: 'Evolução de Enfermagem',
            description:
                'Registro de evoluções com metodologia SOAPIE e diagnósticos',
            icon: <MdDescription className="text-4xl" />,
            route: '/hospital/evolucoes',
            color: 'from-teal-500 to-teal-600',
        },
        {
            id: 'discharge',
            name: 'Alta Hospitalar',
            description:
                'Processo de alta com sumário, orientações e documentação',
            icon: <MdExitToApp className="text-4xl" />,
            route: '/hospital/discharges',
            color: 'from-indigo-500 to-indigo-600',
        },
    ]

    return (
        <div className="flex flex-col gap-6 p-4">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-secondary to-secondary/70 flex items-center justify-center shadow-lg">
                        <MdLocalHospital className="text-2xl text-white" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-base-content">
                            Gestão Hospitalar
                        </h1>
                        <p className="text-sm text-base-content/60">
                            Gerencie internações, leitos e cuidados de enfermagem
                        </p>
                    </div>
                </div>
            </div>

            <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-4">
                <p className="text-sm text-base-content/60 mb-2">Hospital ativo</p>
                <HospitalSelector
                    hospitals={hospitals}
                    selectedId={selectedHospitalId}
                    onChange={setSelected}
                    loading={loadingHospitals}
                />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {hospitalCards.map(card => (
                    <div
                        key={card.id}
                        onClick={() => navigate(card.route)}
                        className="group cursor-pointer bg-base-100 rounded-xl border border-base-300/50 overflow-hidden hover:shadow-lg transition-all duration-300"
                    >
                        <div
                            className={`bg-gradient-to-br ${card.color} p-6 text-white`}
                        >
                            <div className="flex items-center justify-between">
                                {card.icon}
                            </div>
                        </div>
                        <div className="p-4">
                            <h3 className="text-lg font-bold text-base-content mb-2">
                                {card.name}
                            </h3>
                            <p className="text-sm text-base-content/70 mb-4 line-clamp-2">
                                {card.description}
                            </p>
                            <div className="flex items-center justify-between">
                                <span className="text-xs text-base-content/50">
                                    Clique para acessar
                                </span>
                                <MdArrowForward className="text-xl text-secondary group-hover:translate-x-1 transition-transform" />
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    )
}
